import { Injectable, DOCUMENT, inject } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { Observable } from 'rxjs';

import { BaseEditService } from '../base';
import { FormatService } from '../format.service';
import { LanguageService } from '../language.service';
import { MenuService } from '../menu.service';
import { UtilityService } from '../utility.service';
import { BooleanStrings } from '../../constants';
// Only static helpers (split / isValidEmail) — the component itself imports nothing that
// leads back here, so this does not create the GridComponent cycle noted below.
import { FormlyRecipientsComponent } from '../../components/formly/formly-recipients/formly-recipients.component';
import { FormlyProps, FormlySummary, IdDescription, ValueText } from '../../models';
import { FormsValidators } from '../../validators';

// import { GridComponent } from '../../components/grids/grid/grid.component';
declare type GridComponent = any;

export type FormlyHideExpression = (field: FormlyFieldConfig) => boolean;
export type FormlyExpressionProperties = Record<string, string | ((field: FormlyFieldConfig) => any) | Observable<any>>;

@Injectable({
  providedIn: 'root',
})
export class FormlyService {
  protected readonly document = inject<Document>(DOCUMENT);
  protected readonly formatService = inject(FormatService);
  protected readonly menuService = inject(MenuService);
  protected readonly utilityService = inject(UtilityService);
  protected readonly languageService = inject(LanguageService);

  isMobile = false;

  text(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
    templateHtml?: (model: any) => string,
  ): FormlyFieldConfig {
    if (this.menuService.isReadOnly) {
      props = { ...props, readonly: true, tabindex: -1 };
    }
    return {
      key,
      type: 'input',
      props: {
        label: this.languageService.translate(label, props?.abbreviatedLabel),
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
        'props.template': (field: FormlyFieldConfig) => (templateHtml ? templateHtml(field.model) : undefined),
      },
      validators: { validation: validation ?? [], ...extraValidators },
      asyncValidators: asyncValidators,
    };
  }

  textReadonly(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
    templateHtml?: (model: any) => string,
  ): FormlyFieldConfig {
    return this.text(
      key,
      label,
      { ...props, readonly: true, tabindex: -1 },
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
      templateHtml,
    );
  }

  /**
   * An email-recipient row (To / Cc / Bcc): chips plus free typing, model value a
   * comma-separated address string. Carries its own validator, so a chip that does not parse
   * fails the field and names the offending entry. `props.actions` puts link-buttons on the
   * row (the email modal's "Contacts…" / "Bcc"); `props.recipientName` names chips.
   */
  recipients(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return {
      key,
      type: 'recipients',
      props: {
        label: this.languageService.translate(label, props?.abbreviatedLabel),
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
      validators: {
        validation: [],
        recipients: {
          expression: (control: AbstractControl) =>
            FormlyRecipientsComponent.split(control.value).every(FormlyRecipientsComponent.isValidEmail),
          message: (_error: any, field: FormlyFieldConfig) => {
            const invalid = FormlyRecipientsComponent.split(field.formControl?.value).find(
              (x) => !FormlyRecipientsComponent.isValidEmail(x),
            );
            return $localize`Invalid email address: ${invalid}`;
          },
        },
      },
    };
  }

  textarea(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return {
      key,
      type: 'textarea',
      props: {
        label: this.languageService.translate(label, props?.abbreviatedLabel),
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
      validators: { validation: validation ?? [], ...extraValidators },
      asyncValidators,
    };
  }

  /**
   * A narrative field the user can format — bold, italic, underline, lists, indent.
   *
   * Use it for notes that PRINT on a document and benefit from structure. ⚠️ Not a drop-in
   * replacement for `textarea()`: the stored value becomes HTML (prefixed with the rich-text
   * marker), so a field whose text is also sent to an external system or written by an import
   * must stay a plain `textarea()`. Shipping Notes, for instance, goes to UPS and DHL.
   */
  richText(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return {
      key,
      type: 'rich-text',
      props: {
        label: this.languageService.translate(label, props?.abbreviatedLabel),
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
      validators: { validation: validation ?? [], ...extraValidators },
      asyncValidators,
    };
  }

  textAreaReadonly(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.textarea(
      key,
      label,
      { ...props, readonly: true, tabindex: -1 },
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
    );
  }

  password(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
    templateHtml?: (model: any) => string,
  ): FormlyFieldConfig {
    if (this.menuService.isReadOnly) {
      props = { ...props, readonly: true, tabindex: -1 };
    }
    return this.text(
      key,
      label,
      { ...props, type: 'password' },
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
      templateHtml,
    );
  }

  date(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.menuService.isReadOnly
      ? this.dateReadonly(
          key,
          label,
          props,
          hideExpression,
          expressionProperties,
          validation,
          extraValidators,
          asyncValidators,
        )
      : {
          key,
          type: 'date',
          props: {
            label: this.languageService.translate(label, props?.abbreviatedLabel),
            ...props,
          },
          expressions: {
            hide: hideExpression,
            ...expressionProperties,
          },
          validators: {
            validation: props?.required ? [FormsValidators.date, ...(validation ?? [])] : (validation ?? []),
            ...extraValidators,
          },
          asyncValidators,
        };
  }

  dateReadonly(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.textReadonly(
      key,
      label,
      props,
      hideExpression,
      expressionProperties,
      props?.required ? [FormsValidators.date, ...(validation ?? [])] : (validation ?? []),
      extraValidators,
      asyncValidators,
      (model: any) => this.formatService.date(model[key]),
    );
  }

  dateTimeReadonly(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.textReadonly(
      key,
      label,
      props,
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
      (model: any) => this.formatService.dateTime(model[key]),
    );
  }

  number(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = [FormsValidators.number],
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.menuService.isReadOnly
      ? this.numberReadonly(key, label, props, hideExpression)
      : {
          key,
          type: 'input',
          props: {
            type: 'number',
            label: this.languageService.translate(label, props?.abbreviatedLabel),
            ...props,
          },
          // Numbers default to 0, but an optional id (e.g. the PrintNode child
          // account) must stay BLANK when unused — 0 is a real value that gets
          // saved and then sent to the API. props.nullable opts out.
          defaultValue: props?.nullable ? undefined : 0,
          expressions: {
            hide: hideExpression,
            ...expressionProperties,
          },
          validators: { validation: validation ?? [], ...extraValidators },
          asyncValidators,
        };
  }

  numberReadonly(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.textReadonly(
      key,
      label,
      props,
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
      // props.decimalPlaces lets a caller widen the display (e.g. 4dp unit prices); defaults to 2.
      (model: any) =>
        model[key] === undefined ? undefined : this.formatService.number(model[key], props?.decimalPlaces),
    );
  }

  integerReadonly(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.textReadonly(
      key,
      label,
      props,
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
      (model: any) => (model[key] === undefined ? undefined : this.formatService.number(model[key], 0)),
    );
  }

  select(
    key: string,
    label: string,
    options: IdDescription[] | Observable<IdDescription[]> | ValueText[],
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    if (this.menuService.isReadOnly) {
      props = { ...props, disabled: true };
    }
    return {
      key,
      type: 'select',
      props: {
        label: this.languageService.translate(label, props?.abbreviatedLabel),
        options: this.utilityService.mapSelectOptions(options),
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
      validators: { validation: validation ?? [], ...extraValidators },
      asyncValidators,
    };
  }

  // A select that holds SEVERAL values. The model field is an ARRAY of ids, so use it
  // where the entity behind the field is a many-to-many (e.g. the processes a fault
  // applies to) rather than a single foreign key.
  multiSelect(
    key: string,
    label: string,
    options: IdDescription[] | Observable<IdDescription[]> | ValueText[],
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return this.select(key, label, options, { ...props, multiple: true }, hideExpression, expressionProperties);
  }

  selectReadonly(
    key: string,
    label: string,
    options: IdDescription[] | Observable<IdDescription[]>,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.select(
      key,
      label,
      options,
      { ...props, disabled: true },
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
    );
  }

  checkbox(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.menuService.isReadOnly
      ? this.readonlyBoolean(
          key,
          label,
          props,
          hideExpression,
          expressionProperties,
          validation,
          extraValidators,
          asyncValidators,
        )
      : {
          key,
          type: 'checkbox',
          props: {
            hideLabel: true,
            indeterminate: false,
            label: this.languageService.translate(label, props?.abbreviatedLabel),
            ...props,
          },
          expressions: {
            hide: hideExpression,
            ...expressionProperties,
          },
          validators: { validation: validation ?? [], ...extraValidators },
          asyncValidators,
        };
  }

  readonlyBoolean(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = [FormsValidators.number],
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.textReadonly(
      key,
      label,
      props,
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
      (model: any) => (model[key] ? BooleanStrings.translatedYes : BooleanStrings.translatedNo),
    );
  }

  radio(
    key: string,
    name: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
  ): FormlyFieldConfig {
    return this.menuService.isReadOnly
      ? this.textReadonly(
          key,
          label,
          props,
          hideExpression,
          expressionProperties,
          validation,
          extraValidators,
          asyncValidators,
        )
      : {
          key,
          type: 'radio',
          props: {
            hideLabel: true,
            indeterminate: false,
            label: this.languageService.translate(label, props?.abbreviatedLabel),
            name,
            ...props,
          },
          expressions: {
            hide: hideExpression,
            ...expressionProperties,
          },
          validators: { validation: validation ?? [], ...extraValidators },
          asyncValidators,
        };
  }

  fileUpload(
    text: string,
    select: (model: any) => void,
    dropdown?: (model: any) => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return {
      type: 'file-upload',
      props: {
        hideLabel: true,
        text,
        select,
        dropdown,
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
    };
  }

  button(
    text: string,
    onClick: (model: any) => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return {
      type: 'button',
      props: {
        hideLabel: true,
        text,
        onClick,
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
    };
  }

  importButton(
    text: string,
    select: (model: any) => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return {
      type: 'import-button',
      props: {
        hideLabel: true,
        text,
        select,
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
    };
  }

  createButton(
    onClick: (model: any) => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    // Overrides the button label where "Create" is not the right verb - e.g. "Add" when
    // the grid attaches existing records rather than making a new one. Defaults to Create.
    text?: string,
  ): FormlyFieldConfig {
    return this.button(
      text ?? $localize`Create`,
      (model: any) => onClick(model),
      { iconClass: 'fa fa-plus', ...props },
      this.menuService.isReadOnly ? () => true : hideExpression,
      expressionProperties,
    );
  }

  exportButton(
    grid: GridComponent,
    fileName: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return this.button(
      $localize`Export`,
      () => grid.export(fileName),
      { btnClass: 'success', iconClass: 'far fa-file-excel', ...props },
      hideExpression,
      expressionProperties,
    );
  }

  exportButtonNoGridComponent(
    onClick: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return this.button(
      $localize`Export`,
      () => onClick(),
      { btnClass: 'success', iconClass: 'far fa-file-excel', ...props },
      hideExpression,
      expressionProperties,
    );
  }

  printButton(
    onClick: (model: any) => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return this.button(
      $localize`Print`,
      (model: any) => onClick(model),
      { iconClass: 'fa fa-print', ...props },
      this.menuService.isReadOnly ? () => true : hideExpression,
      expressionProperties,
    );
  }

  backButton(route: ActivatedRoute, path?: string) {
    return {
      type: 'back-button',
      props: {
        route,
        path,
      },
    };
  }

  readonly(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    templateHtml?: (model: any) => string,
  ): FormlyFieldConfig {
    return {
      type: 'template',
      props: {
        label: this.languageService.translate(label, props?.abbreviatedLabel),
        ...props,
      },
      expressions: {
        hide: hideExpression,
        'props.template': (field: FormlyFieldConfig) => (templateHtml ? templateHtml(field.model) : field.model?.[key]),
      },
    };
  }

  template(
    label: string,
    template: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig {
    return {
      type: 'template',
      props: {
        label,
        template,
        ...props,
      },
      expressions: {
        hide: hideExpression,
      },
    };
  }

  requiredFieldValidation(key: string, label: string) {
    return this.fieldValidation(key, label, { required: true }, (field: FormlyFieldConfig) => !!field.model?.[key]);
  }

  fieldValidation(key: string, label: string, props?: FormlyProps, hideExpression?: FormlyHideExpression) {
    return {
      key,
      type: 'template',
      props: {
        ...props,
        label,
        hideLabel: true,
      },
      hideExpression: hideExpression,
    };
  }

  warningMessage(message: string, hideExpression?: FormlyHideExpression) {
    return {
      type: 'template',
      props: {
        template: `<div class="error-message">${message}</div>`,
      },
      expressions: {
        hide: hideExpression,
      },
    };
  }

  lineBreak() {
    return {
      type: 'line-break',
    };
  }

  fields(label: string, fields: any[], props?: FormlyProps, hideExpression?: FormlyHideExpression): FormlyFieldConfig {
    fields.forEach((x) => (x.wrappers = []));

    return {
      type: 'fields',
      fieldGroup: fields,
      fieldGroupClassName: 'row',
      props: {
        label,
        ...props,
      },
      expressions: {
        hide: hideExpression,
      },
    };
  }

  array(
    key: string,
    fields: FormlyFieldConfig[],
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig {
    return {
      key,
      type: 'array',
      fieldArray: {
        fieldGroup: fields,
      },
      props,
      expressions: {
        hide: hideExpression,
      },
    };
  }

  table(
    key: string,
    fields: FormlyFieldConfig[],
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig {
    fields.forEach((field: FormlyFieldConfig) => (field.wrappers = []));
    return {
      key,
      type: 'repeat',
      fieldArray: {
        fieldGroup: fields,
      },
      props,
      expressions: {
        hide: hideExpression,
      },
    };
  }

  summary(fields: FormlySummary[], props?: FormlyProps, hideExpression?: FormlyHideExpression): FormlyFieldConfig {
    fields.forEach((field: FormlyFieldConfig) => (field.wrappers = []));
    return {
      type: 'summary',
      props: {
        fields,
        ...props,
      },
      expressions: {
        hide: hideExpression,
      },
    };
  }

  inlineFormLineBreak(): FormlyFieldConfig {
    return {
      type: 'template',
      className: 'flex-break',
    };
  }

  mobileSplitFields(fields: FormlyFieldConfig[]) {
    fields.forEach((field: FormlyFieldConfig) => {
      field.props.mobileSplit = true;
    });
    return fields;
  }

  oneColumnGrid(column1: FormlyFieldConfig[]): FormlyFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-xl-6',
            fieldGroup: column1,
          },
        ],
      },
    ];
  }

  /**
   * One column spanning the FULL width, rather than `oneColumnGrid`'s half (`col-xl-6`).
   *
   * Use it where the field is long free text — a narrative, a standard-text block, an address
   * — and the half-width column makes the box needlessly narrow. Everything else about the
   * layout is identical, so it is a drop-in swap for `oneColumnGrid`.
   */
  fullWidthGrid(column1: FormlyFieldConfig[]): FormlyFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12',
            fieldGroup: column1,
          },
        ],
      },
    ];
  }

  twoColumnGrid(column1: FormlyFieldConfig[], column2: FormlyFieldConfig[]): FormlyFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-xl-6',
            fieldGroup: column1,
          },
          {
            className: 'col-xl-6',
            fieldGroup: column2,
          },
        ],
      },
    ];
  }

  threeColumnGrid(
    column1: FormlyFieldConfig[],
    column2: FormlyFieldConfig[],
    column3: FormlyFieldConfig[],
  ): FormlyFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-xl-4',
            fieldGroup: column1,
          },
          {
            className: 'col-xl-4',
            fieldGroup: column2,
          },
          {
            className: 'col-xl-4',
            fieldGroup: column3,
          },
        ],
      },
    ];
  }

  fourColumnGrid(
    column1: FormlyFieldConfig[],
    column2: FormlyFieldConfig[],
    column3: FormlyFieldConfig[],
    column4: FormlyFieldConfig[],
  ): FormlyFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-xl-3',
            fieldGroup: column1,
          },
          {
            className: 'col-xl-3',
            fieldGroup: column2,
          },
          {
            className: 'col-xl-3',
            fieldGroup: column3,
          },
          {
            className: 'col-xl-3',
            fieldGroup: column4,
          },
        ],
      },
    ];
  }

  twoColumnMobileGrid(column1: FormlyFieldConfig[], column2: FormlyFieldConfig[]): FormlyFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-6',
            fieldGroup: this.mobileSplitFields(column1),
          },
          {
            className: 'col-6',
            fieldGroup: this.mobileSplitFields(column2),
          },
        ],
      },
    ];
  }

  search(
    key: string,
    label?: string,
    service?: BaseEditService<any, any>,
    results?: (results: Observable<any[]>) => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
  ): FormlyFieldConfig {
    return {
      key,
      type: 'input-search',
      props: {
        label,
        service,
        results,
        ...props,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
    };
  }

  modalSearch(
    key: string,
    label: string,
    service: BaseEditService<any, any>,
    columnDefs: ColDef[],
    selected?: (model: any) => void,
    clear?: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    id?: (model: any) => any,
    id2?: (model: any) => any,
    allLabel?: string,
    createComponent?: any,
  ): FormlyFieldConfig {
    return {
      key,
      type: 'input-search',
      props: {
        label,
        allLabel,
        service,
        selected,
        clear,
        columnDefs,
        openModal: true,
        editButton: false,
        createComponent,
        ...props,
        required: false,
      },
      expressions:
        id && id2
          ? {
              hide: this.menuService.isReadOnly ? () => true : hideExpression,
              'props.id': (field: FormlyFieldConfig) => id(field.model),
              'props.id2': (field: FormlyFieldConfig) => id2(field.model),
            }
          : id
            ? {
                hide: this.menuService.isReadOnly ? () => true : hideExpression,
                'props.id': (field: FormlyFieldConfig) => id(field.model),
              }
            : {
                hide: this.menuService.isReadOnly ? () => true : hideExpression,
              },
    };
  }

  focusElement(id: string) {
    setTimeout(() => {
      const input: HTMLElement = document.querySelector(`input[id*="_${id}_"]`);
      input?.focus();
    }, 0);
  }

  selectElement(id: string) {
    setTimeout(() => {
      const input: HTMLInputElement = document.querySelector(`input[id*="_${id}_"]`);
      input?.select();
    }, 0);
  }

  fullWidthTextarea(
    key: string,
    label: string,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    expressionProperties?: FormlyExpressionProperties,
    validation: any[] = undefined,
    extraValidators: any = undefined,
    asyncValidators: any = undefined,
    rowClassName = 'row mt-2',
  ): FormlyFieldConfig[] {
    const field = this.textarea(
      key,
      '',
      props,
      hideExpression,
      expressionProperties,
      validation,
      extraValidators,
      asyncValidators,
    );

    field.wrappers = [];
    field.className = 'col-12';

    const labelField: FormlyFieldConfig = {
      type: 'template',
      className: 'col-12',
      wrappers: [],
      props: {
        template: `<label class="form-label" style="display:block; text-align:left;">${this.languageService.translate(
          label,
          props?.abbreviatedLabel,
        )}</label>`,
      },
      expressions: {
        hide: hideExpression,
        ...expressionProperties,
      },
    };

    return [
      {
        fieldGroupClassName: rowClassName,
        fieldGroup: [labelField, field],
      },
    ];
  }
}
